import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import "./ListPageStatsBar.css";

export type ListPageStatsState = {
  /** Ornek: kursiyer, egitmen, arac */
  label: string;
  visible: number;
  total?: number;
  loading?: boolean;
};

type ListPageStatsContextValue = {
  stats: ListPageStatsState | null;
  setStats: (stats: ListPageStatsState | null) => void;
};

const ListPageStatsContext = createContext<ListPageStatsContextValue | null>(null);

export function ListPageStatsProvider({ children }: { children: ReactNode }) {
  const [stats, setStats] = useState<ListPageStatsState | null>(null);
  const value = useMemo(() => ({ stats, setStats }), [stats]);
  return (
    <ListPageStatsContext.Provider value={value}>{children}</ListPageStatsContext.Provider>
  );
}

export function useRegisterListPageStats(stats: ListPageStatsState | null) {
  const ctx = useContext(ListPageStatsContext);
  if (!ctx) return;

  const { setStats } = ctx;
  useEffect(() => {
    setStats(stats);
    return () => setStats(null);
  }, [setStats, stats?.label, stats?.visible, stats?.total, stats?.loading]);
}

function formatStatsText(stats: ListPageStatsState): string {
  if (stats.loading) return "Yükleniyor…";
  const total = stats.total ?? stats.visible;
  if (total !== stats.visible) {
    return `${stats.visible} / ${total} ${stats.label} listeleniyor`;
  }
  return `${stats.visible} ${stats.label} listeleniyor`;
}

export function ListPageStatsBar() {
  const ctx = useContext(ListPageStatsContext);
  if (!ctx?.stats) return null;

  return (
    <div className="kp-list-stats" role="status" aria-live="polite">
      <span className="kp-list-stats__dot" aria-hidden />
      {formatStatsText(ctx.stats)}
    </div>
  );
}
